import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { userQueryOptions } from '@/core/auth/user-query'
import { useAuthStore } from '@/features/auth/store'
import { configQueryOptions } from './use-config'
import { usePostDecisionUserRead } from './use-post-decision-user'

// Warms the GrowthBook config cache the moment the user is authenticated, so
// the experience gates (membership-info / cancel flow) resolve without a
// loading flash — including the mobile → /autologin → /membership-info handoff,
// where auth flips true before the gated route mounts. Mounted once at the root.
//
// It also owns the whole me → /config → me ordering: /config runs the
// start-flow split decision as a side effect, so the user read that follows it
// lives here rather than in whichever feature happens to consume the verdict.
export function FeatureFlagsPrefetch() {
  const queryClient = useQueryClient()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // Drive the "me" query so it runs even on the persisted-session boot path,
  // where isAuthenticated flips true WITHOUT it (initial load / initializeSession).
  // /config must never precede the authed user being established — the backend
  // identifies experiment assignments in Amplitude keyed off that user. Gating
  // the prefetch on `me` success guarantees that ordering in every entry path.
  const { isSuccess: meReady } = useQuery({
    ...userQueryOptions,
    enabled: isAuthenticated,
  })

  useEffect(() => {
    if (!isAuthenticated || !meReady) return
    queryClient.prefetchQuery(configQueryOptions)
  }, [isAuthenticated, meReady, queryClient])

  // The user read that follows the decision the request above just triggered.
  // It belongs here, next to the request it waits on: the takeover redirect and
  // the exposure report the plan surfaces mount both consume the result, and
  // neither is a place the ordering can live without one feature owning
  // correctness for the other.
  usePostDecisionUserRead()

  return null
}
